import { useCallback, useEffect, useState } from "react";

/**
 * Plays the <audio> in audioRef while `active` is true (the confirmation
 * screen) and stops it as soon as the user does anything:
 *  - clicks / taps anywhere, or presses a key
 *  - hovers a button or link
 *  - switches to another tab (e.g. Zoom opened in a new tab)
 *  - leaves the screen
 *
 * Returns { soundPlaying, stopSound } in case a screen needs to stop it manually.
 */
export default function useStoppableSound(audioRef, active, { volume = 0.25 } = {}) {
  const [soundPlaying, setSoundPlaying] = useState(false);

  const stopSound = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setSoundPlaying(false);
  }, [audioRef]);

  useEffect(() => {
    if (!active) return undefined;

    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = 0;
      audio.volume = volume;
      const playing = audio.play();
      if (playing && playing.then) {
        playing
          .then(() => setSoundPlaying(true))
          .catch((err) => {
            console.log("Autoplay blocked:", err);
          });
      } else {
        setSoundPlaying(true);
      }
    }

    const stopOnInteraction = () => stopSound();
    const stopOnHover = (event) => {
      const target = event.target;
      if (target && target.closest && target.closest("a, button, [role='button']")) {
        stopSound();
      }
    };
    const stopWhenHidden = () => {
      if (document.hidden) stopSound();
    };
    const handleEnded = () => setSoundPlaying(false);

    document.addEventListener("pointerdown", stopOnInteraction, true);
    document.addEventListener("touchstart", stopOnInteraction, true);
    document.addEventListener("keydown", stopOnInteraction, true);
    document.addEventListener("pointerover", stopOnHover, true);
    document.addEventListener("visibilitychange", stopWhenHidden);
    if (audio) audio.addEventListener("ended", handleEnded);

    return () => {
      document.removeEventListener("pointerdown", stopOnInteraction, true);
      document.removeEventListener("touchstart", stopOnInteraction, true);
      document.removeEventListener("keydown", stopOnInteraction, true);
      document.removeEventListener("pointerover", stopOnHover, true);
      document.removeEventListener("visibilitychange", stopWhenHidden);
      if (audio) audio.removeEventListener("ended", handleEnded);
      stopSound();
    };
  }, [active, audioRef, stopSound, volume]);

  return { soundPlaying, stopSound };
}
